import React, {useState, useEffect } from 'react';
import {StallFormData, SubscriptionDuration } from './BuyStall';
import {GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

import {
  BuyStallForm,
  FormGroup,
  Label,
  Input,
  TextArea,
  Button,
  FormSection,
  FormRow,
  FileUploadContainer,
  FileUploadLabel,
  FileInput,
  PreviewImage,
  ImagePreviewContainer,
  RequiredIndicator,
  Select,
  ErrorMessage,
  RadioContainer,
  RadioLabel,
  RadioInput,
  FormContentWrapper,
  SectionTitle
} from './styles/StallRegistrationForm.styles';

import {FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, 
  faMapMarkerAlt, 
  faPhone, 
  faEnvelope, 
  faGlobe, 
  faImage, 
  faMoneyBillWave, 
  faTags 
} from '@fortawesome/free-solid-svg-icons';
import {
  faTiktok,
  faFacebook,
  faXTwitter,
  faYoutube,
  faInstagram,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';
import MapPicker from '../../components/mapPicker';

interface AdministrativeUnit {
  id: string;
  name: string;
  country?: string;
  region?: string;
  district?: string;
  subcounty?: string;
  parish?: string;
  type: string;
}

interface StallRegistrationFormProps {
  formData: StallFormData;
  setFormData: (data: Partial<StallFormData> | ((prev: StallFormData) => Partial<StallFormData>)) => void;
  onPayment: () => void;
  locationData: any; // LocationData type from BuyStall
}

const StallRegistrationForm: React.FC<StallRegistrationFormProps> = ({
  formData,
  setFormData,
  onPayment,
  locationData
}) => {
  const [errors, setErrors] = useState<{[key: string]: string }>({});
  const [services, setServices] = useState<string[]>(formData.additionalDetails ? formData.additionalDetails.split(',').map(s => s.trim()) : ['']);
  const [regions, setRegions] = useState<AdministrativeUnit[]>([]);
  const [districts, setDistricts] = useState<AdministrativeUnit[]>([]);
  const [subcounties, setSubcounties] = useState<AdministrativeUnit[]>([]);
  const [parishes, setParishes] = useState<AdministrativeUnit[]>([]);
  const [villages, setVillages] = useState<string[]>([]);
const allCountries = [
  'Uganda', 'Kenya', 'Tanzania', 'Rwanda', 'Burundi',
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 
  'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Cambodia', 'Cameroon', 'Canada', 
  'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 
  'Colombia', 'Comoros', 'Congo (Brazzaville)', 'Congo (Kinshasa)', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark',
  'Djibouti', 'Dominica', 'Dominican Republic', 'East Timor', 'Ecuador',
  'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
  'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan',
  'Jordan', 'Kazakhstan', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia',
  'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
  'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
  'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria',
  'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
  'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa',
  'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia',
  'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden',
  'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Thailand',
  'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
  'Turkmenistan', 'Tuvalu', 'Ukraine', 'United Arab Emirates', 'United Kingdom',
  'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City',
  'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

  // Use useEffect to populate regions based on locationData
  useEffect(() => {
    const { selectedCountry } = formData;
    if (selectedCountry === 'Uganda' && locationData) {
      const regionNames = Object.keys(locationData);
      const fetchedRegions = regionNames.map(name => ({
        id: name,
        name,
        type: 'region',
      })) as AdministrativeUnit[];
      setRegions(fetchedRegions);
    } else {
      setRegions([]);
    }
  }, [formData.selectedCountry, locationData]);

  // Use useEffect to populate districts based on locationData
  useEffect(() => {
    const { selectedRegion } = formData;
    if (selectedRegion && locationData && locationData[selectedRegion]) {
      const districtNames = Object.keys(locationData[selectedRegion]);
      const fetchedDistricts = districtNames.map(name => ({
        id: name,
        name,
        type: 'district',
      })) as AdministrativeUnit[];
      setDistricts(fetchedDistricts);
    } else {
      setDistricts([]);
    }
  }, [formData.selectedRegion, locationData]);

  // Use useEffect to populate subcounties based on locationData
  useEffect(() => {
    const { selectedRegion, selectedDistrict } = formData;
    if (selectedDistrict && locationData && locationData[selectedRegion] && locationData[selectedRegion][selectedDistrict]) {
      const subcountyNames = Object.keys(locationData[selectedRegion][selectedDistrict]);
      const fetchedSubcounties = subcountyNames.map(name => ({
        id: name,
        name,
        type: 'subcounty',
      })) as AdministrativeUnit[];
      setSubcounties(fetchedSubcounties);
    } else {
      setSubcounties([]);
    }
  }, [formData.selectedDistrict, formData.selectedRegion, locationData]);

  // Use useEffect to populate parishes based on locationData
  useEffect(() => {
    const { selectedRegion, selectedDistrict, selectedSubcounty } = formData;
    if (selectedSubcounty && locationData && locationData[selectedRegion] && locationData[selectedRegion][selectedDistrict] && locationData[selectedRegion][selectedDistrict][selectedSubcounty]) {
      const parishNames = Object.keys(locationData[selectedRegion][selectedDistrict][selectedSubcounty]);
      const fetchedParishes = parishNames.map(name => ({
        id: name,
        name,
        type: 'parish',
      })) as AdministrativeUnit[];
      setParishes(fetchedParishes);
    } else {
      setParishes([]);
    }
  }, [formData.selectedSubcounty, formData.selectedRegion, formData.selectedDistrict, locationData]);

  // Use useEffect to populate villages based on locationData
  useEffect(() => {
    const { selectedRegion, selectedDistrict, selectedSubcounty, selectedParish } = formData;
    if (selectedParish && locationData && locationData[selectedRegion] && locationData[selectedRegion][selectedDistrict] && locationData[selectedRegion][selectedDistrict][selectedSubcounty] && locationData[selectedRegion][selectedDistrict][selectedSubcounty][selectedParish]) {
      const villageNames = locationData[selectedRegion][selectedDistrict][selectedSubcounty][selectedParish];
      setVillages(villageNames);
    } else {
      setVillages([]);
    }
  }, [formData.selectedParish, formData.selectedRegion, formData.selectedDistrict, formData.selectedSubcounty, locationData]);

  useEffect(() => {
    const joined = services.filter(s => s.trim()).join(', ');
    setFormData({ additionalDetails: joined });
  }, [services, setFormData]);

  const handleBusinessPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData({businessPhoto: file, businessPhotoPreview: URL.createObjectURL(file) });
      setErrors(prev => ({...prev, businessPhoto: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string } = {};

    if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required.';
    if (!formData.contactPerson.trim()) newErrors.contactPerson = 'Contact person is required.';
    if (!formData.businessPhone.trim()) newErrors.businessPhone = 'Business phone is required.';
    if (formData.businessPhone.trim() && !/^\+?\d{10,15}$/.test(formData.businessPhone.trim())) {
      newErrors.businessPhone = 'Please enter a valid phone number (e.g., +2567...).';
    }
    if (formData.email.trim() && !/\S+@\S+\.\S/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!formData.stallType) newErrors.stallType = 'Stall type is required.';

    if (!formData.actualLocation.trim()) newErrors.actualLocation = 'Actual location is required.';

    if (!formData.category.trim()) newErrors.category = 'Category is required.';
    if (!formData.businessPhoto) newErrors.businessPhoto = 'A clear photo of your business is required.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onPayment();
    } else {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        document.getElementById(firstErrorField)?.scrollIntoView({behavior: 'smooth', block: 'center' });
      }
    }
  };

const handleLocationSelect = (lat: number, lng: number) => {
  setFormData({latitude: lat, longitude: lng });
};

const addService = () => {
  setServices(prev => [...prev, '']);
};

const removeService = (index: number) => {
  setServices(prev => prev.filter((_, i) => i !== index));
};

const updateService = (index: number, value: string) => {
  setServices(prev => prev.map((service, i) => i === index ? value : service));
};

  return (
    <BuyStallForm onSubmit={handleSubmit}>
      <FormContentWrapper>
        
        <FormSection>
          <SectionTitle>
            <FontAwesomeIcon icon={faBuilding} />
            <h3>Business Profile</h3>
          </SectionTitle>

          <FormRow>
            <FormGroup error={!!errors.businessName}>
              <Label htmlFor="businessName">Business Name <RequiredIndicator>*</RequiredIndicator></Label>
              <Input
                type="text"
                id="businessName"
                value={formData.businessName}
                onChange={(e) => {
                  setFormData({businessName: e.target.value });
                  setErrors(prev => ({...prev, businessName: '' }));
                }}
                required
                placeholder="Your business or shop name"
              />
              {errors.businessName && <ErrorMessage>{errors.businessName}</ErrorMessage>}
            </FormGroup>

            <FormGroup error={!!errors.contactPerson}>
              <Label htmlFor="contactPerson">Contact Person <RequiredIndicator>*</RequiredIndicator></Label>
              <Input
                type="text"
                id="contactPerson"
                value={formData.contactPerson}
                onChange={(e) => {
                  setFormData({contactPerson: e.target.value });
                  setErrors(prev => ({...prev, contactPerson: '' }));
                }}
                required
                placeholder="Name of the person to contact"
              />
              {errors.contactPerson && <ErrorMessage>{errors.contactPerson}</ErrorMessage>}
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup error={!!errors.businessPhone}>
              <Label htmlFor="businessPhone"><FontAwesomeIcon icon={faPhone} /> Business Phone <RequiredIndicator>*</RequiredIndicator></Label>
              <Input
                type="tel"
                id="businessPhone"
                value={formData.businessPhone}
                onChange={(e) => {
                  setFormData({businessPhone: e.target.value });
                  setErrors(prev => ({...prev, businessPhone: '' }));
                }}
                required
                placeholder="+256701234567"
              />
              {errors.businessPhone && <ErrorMessage>{errors.businessPhone}</ErrorMessage>}
            </FormGroup>

            <FormGroup error={!!errors.email}>
              <Label htmlFor="email"><FontAwesomeIcon icon={faEnvelope} /> Email</Label>
              <Input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({email: e.target.value });
                  setErrors(prev => ({...prev, email: '' }));
                }}
                placeholder="business@example.com"
              />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label htmlFor="whatsappPhone">WhatsApp Phone</Label>
              <Input
                type="tel"
                id="whatsappPhone"
                value={formData.whatsappPhone}
                onChange={(e) => setFormData({whatsappPhone: e.target.value })}
                placeholder="+256701234567 (optional)"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="website"><FontAwesomeIcon icon={faGlobe} /> Website</Label>
              <Input
                type="url"
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({website: e.target.value })}
                placeholder="https://yourbusiness.com (optional)"
              />
            </FormGroup>
          </FormRow>

          {/* Social media links moved here */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="tiktokLink"><FontAwesomeIcon icon={faTiktok} /> TikTok</Label>
              <Input
                type="url"
                id="tiktokLink"
                value={formData.tiktokLink || ''}
                onChange={(e) => setFormData({tiktokLink: e.target.value })}
                placeholder="https://tiktok.com/@yourbusiness"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="facebookLink"><FontAwesomeIcon icon={faFacebook} /> Facebook</Label>
              <Input
                type="url"
                id="facebookLink"
                value={formData.facebookLink || ''}
                onChange={(e) => setFormData({facebookLink: e.target.value })}
                placeholder="https://facebook.com/yourbusiness"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="xTwitterLink"><FontAwesomeIcon icon={faXTwitter} /> X (Twitter)</Label>
              <Input
                type="url"
                id="xTwitterLink"
                value={formData.xTwitterLink || ''}
                onChange={(e) => setFormData({xTwitterLink: e.target.value })}
                placeholder="https://x.com/yourbusiness"
              />
            </FormGroup>
             <FormGroup>
              <Label htmlFor="linkedinLink"><FontAwesomeIcon icon={faLinkedin} /> LinkedIn</Label>
              <Input
                type="url"
                id="linkedinLink"
                value={formData.linkedinLink || ''}
                onChange={(e) => setFormData({linkedinLink: e.target.value })}
                placeholder="https://linkedin.com/@yourchannel"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="youtubeLink"><FontAwesomeIcon icon={faYoutube} /> YouTube</Label>
              <Input
                type="url"
                id="youtubeLink"
                value={formData.youtubeLink || ''}
                onChange={(e) => setFormData({youtubeLink: e.target.value })}
                placeholder="https://youtube.com/@yourchannel"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="instagramLink"><FontAwesomeIcon icon={faInstagram} /> Instagram</Label>
              <Input
                type="url"
                id="instagramLink"
                value={formData.instagramLink || ''}
                onChange={(e) => setFormData({instagramLink: e.target.value })}
                placeholder="https://instagram.com/yourbusiness"
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup error={!!errors.category}>
              <Label htmlFor="category"><FontAwesomeIcon icon={faTags} /> Category <RequiredIndicator>*</RequiredIndicator></Label>
            <Select
                id="category"
                value={formData.category}
                onChange={(e) => {
                  setFormData({category: e.target.value });
                  setErrors(prev => ({...prev, category: '' }));
                }}
                required
              >
               <option value="">Select Category</option>
              <option value="Boutique Businesses">Boutique Businesses</option>
              <option value="Electronic Shops/Stores">Electronic Shops/Stores</option>
              <option value="Supermarkets">Supermarkets</option>
              <option value="Hardware stores/shops">Hardware stores/shops</option>
              <option value="Food manufacturers & distributors">Food manufacturers & distributors</option>
              <option value="Health & Wellness products distributors">Health & Wellness products distributors</option>
              <option value="Hotels, Lodges and Guest House Businesses">Hotels, Lodges and Guest House Businesses</option>
              <option value="Airbnb">Airbnb</option>
              <option value="Construction Companies">Construction Companies</option>
              <option value="Interior Designers">Interior Designers</option>
              <option value="Tailors & fashion Designers">Tailors & fashion Designers</option>
              <option value="Clothes, Text tile, Shoes and Beddings Businesses">Clothes, Text tile, Shoes and Beddings Businesses</option>
              <option value="Restaurants & Cafes">Restaurants & Cafes</option>
              <option value="Catering Service Providers">Catering Service Providers</option>
              <option value="Travel & Tour Companies">Travel & Tour Companies</option>
              <option value="Energy Suppliers (Including solar)">Energy Suppliers (Including solar)</option>
              <option value="Consultancy Firms">Consultancy Firms</option>
              <option value="Insurance Companies">Insurance Companies</option>
              <option value="Tax Consultants">Tax Consultants</option>
              <option value="Law Firms">Law Firms</option>
              <option value="Car Hire & Transport Service Providers">Car Hire & Transport Service Providers</option>
              <option value="Retail Shops">Retail Shops</option>
              <option value="Grocery Businesses">Grocery Businesses</option>
              <option value="Drinks manufacturers and distributors (water, soft drinks, beer, wines, spirits and other beverages)">Drinks manufacturers and distributors (water, soft drinks, beer, wines, spirits and other beverages)</option>
              <option value="Hung out places, Bars and Leisure Gardens">Hung out places, Bars and Leisure Gardens</option>
              <option value="Event Venues (wedding halls, Gardens & Concert venues)">Event Venues (wedding halls, Gardens & Concert venues)</option>
              <option value="Recreation Centres (Sand Beaches, Leisure Parks, resorts, etc)">Recreation Centres (Sand Beaches, Leisure Parks, resorts, etc)</option>
              <option value="Sports betting & gambling Companies">Sports betting & gambling Companies</option>
              <option value="Cinema Halls">Cinema Halls</option>
              <option value="Hospitals, Clinics, Drug shops & Pharmacies">Hospitals, Clinics, Drug shops & Pharmacies</option>
              <option value="Health Clubs (Fitness & Gyms, Sauna, Steam, Spa and Massage)">Health Clubs (Fitness & Gyms, Sauna, Steam, Spa and Massage)</option>
              <option value="Wholesale shops">Wholesale shops</option>
              <option value="Auto Services Shops (Parts, Accessories & Repairs)">Auto Services Shops (Parts, Accessories & Repairs)</option>
              <option value="Oil and Gas Companies (Including fuel stations)">Oil and Gas Companies (Including fuel stations)</option>
              <option value="Movers/Removalists">Movers/Removalists</option>
              <option value="Recruitment Firms/Agencies (Jobs & Career Opportunities)">Recruitment Firms/Agencies (Jobs & Career Opportunities)</option>
              <option value="Furniture and Metal works Businesses">Furniture and Metal works Businesses</option>
              <option value="Home Appliance Businesses">Home Appliance Businesses</option>
              <option value="Decorations (Decor) Service Providers">Decorations (Decor) Service Providers</option>
              <option value="Gardens & Compound Designers">Gardens & Compound Designers</option>
              <option value="Cleaning and Fumigation Businesses">Cleaning and Fumigation Businesses</option>
              <option value="Laundry & Dry Cleaners">Laundry & Dry Cleaners</option>
              <option value="Beauty Shops (Cosmetics, Fragrances and perfumes, Skincare & skincare products, Hair Salon & Hair-care products, Nails care and Nails care products, Makeup services)">Beauty Shops (Cosmetics, Fragrances and perfumes, Skincare & skincare products, Hair Salon & Hair-care products, Nails care and Nails care products, Makeup services)</option>
              <option value="Jewellery & Gift Shops">Jewellery & Gift Shops</option>
              <option value="Logistics, Clearing & Forwarding Companies">Logistics, Clearing & Forwarding Companies</option>
              <option value="Telecom & Network Companies">Telecom & Network Companies</option>
              <option value="Schools, Institutes, Colleges & Universities">Schools, Institutes, Colleges & Universities</option>
              <option value="Land and Buildings">Land and Buildings</option>
              <option value="Car Dealers">Car Dealers</option>
              <option value="Importers and Exporters">Importers and Exporters</option>
              <option value="Audit & Accounting Firms">Audit & Accounting Firms</option>
              <option value="IT Service Providers">IT Service Providers</option>
              <option value="Electrical Engineering Companies">Electrical Engineering Companies</option>
              <option value="Plumbing Companies">Plumbing Companies</option>
              <option value="Mechanical Engineering Companies">Mechanical Engineering Companies</option>
              <option value="Factories & Processing Companies">Factories & Processing Companies</option>
              <option value="Photocopying, Printing & Branding Companies">Photocopying, Printing & Branding Companies</option>
              <option value="Event Planners">Event Planners</option>
              <option value="Sound & Production Service Providers">Sound & Production Service Providers</option>
              <option value="Photography & Videography Service Providers">Photography & Videography Service Providers</option>
              <option value="Artists, Performers & Cultural Dancers">Artists, Performers & Cultural Dancers</option>
              <option value="Fishing Companies">Fishing Companies</option>
              <option value="Farm Produce Suppliers">Farm Produce Suppliers</option>
              <option value="Livestock & Livestock Products Suppliers">Livestock & Livestock Products Suppliers</option>
              <option value="General Equipment Suppliers">General Equipment Suppliers</option>
              <option value="Agro Suppliers (Seeds, Fertilizers & other Agricultural products)">Agro Suppliers (Seeds, Fertilizers & other Agricultural products)</option>
              <option value="Poultry & Poultry Products Suppliers (Chicken, Eggs)">Poultry & Poultry Products Suppliers (Chicken, Eggs)</option>
              <option value="Airline Booking and Visa Companies">Airline Booking and Visa Companies</option>
              <option value="Other Businesses/Companies/Organisations">Other Businesses/Companies/Organisations</option>
              </Select>
              {errors.category && <ErrorMessage>{errors.category}</ErrorMessage>}
            </FormGroup>
          </FormRow>

        </FormSection>
{/*  */}
<FormSection>
  <SectionTitle>
    <FontAwesomeIcon icon={faMoneyBillWave} />
    <h3>Stall Type & Subscription</h3>
  </SectionTitle>

  <FormRow>
    <FormGroup error={!!errors.stallType}>
      <Label>Stall Type <RequiredIndicator>*</RequiredIndicator></Label>
      <RadioContainer>
        <RadioLabel>
          <RadioInput
            type="radio"
            name="stallType"
            value="Premium"
            checked={formData.stallType === 'Premium'}
            onChange={(e) => {
              setFormData({stallType: e.target.value as 'Premium' | 'Standard' });
              setErrors(prev => ({...prev, stallType: '' }));
            }}
          />
          Premium
        </RadioLabel>
        <RadioLabel>
          <RadioInput
            type="radio"
            name="stallType"
            value="Standard"
            checked={formData.stallType === 'Standard'}
            onChange={(e) => {
              setFormData({stallType: e.target.value as 'Premium' | 'Standard' });
              setErrors(prev => ({...prev, stallType: '' }));
            }}
          />
          Standard
        </RadioLabel>
      </RadioContainer>
      {errors.stallType && <ErrorMessage>{errors.stallType}</ErrorMessage>}
    </FormGroup>
  </FormRow>

  {/* New section for subscription duration */}
  {formData.stallType && (
    <FormRow>
      <FormGroup error={!!errors.subscriptionDuration}>
    <Label>Subscription Period <RequiredIndicator>*</RequiredIndicator></Label>
    <RadioContainer>          
          <RadioLabel>
            <RadioInput
              type="radio"
              name="subscriptionDuration"
              value="Monthly"
              checked={formData.subscriptionDuration === 'Monthly'}
              onChange={(e) => {
                setFormData({subscriptionDuration: e.target.value as SubscriptionDuration });
                setErrors(prev => ({...prev, subscriptionDuration: '' }));
              }}
            />
            Monthly
          </RadioLabel>
          <RadioLabel>
            <RadioInput
              type="radio"
              name="subscriptionDuration"
              value="6-Monthly"
              checked={formData.subscriptionDuration === '6-Monthly'}
              onChange={(e) => {
                setFormData({subscriptionDuration: e.target.value as SubscriptionDuration });
                setErrors(prev => ({...prev, subscriptionDuration: '' }));
              }}
            />
            6-Monthly (20% off)
          </RadioLabel>
          <RadioLabel>
            <RadioInput
              type="radio"
              name="subscriptionDuration"
              value="12-Monthly"
              checked={formData.subscriptionDuration === '12-Monthly'}
              onChange={(e) => {
                setFormData({subscriptionDuration: e.target.value as SubscriptionDuration });
                setErrors(prev => ({...prev, subscriptionDuration: '' }));
              }}
            />
            12-Monthly (30% off)
          </RadioLabel>
        </RadioContainer>
        {errors.subscriptionDuration && <ErrorMessage>{errors.subscriptionDuration}</ErrorMessage>}
      </FormGroup>
    </FormRow>
  )}
</FormSection>
{/*  */}
     
<FormSection>
  <SectionTitle>
    <FontAwesomeIcon icon={faMapMarkerAlt} />
    <h3>Business Location(This is important for customers to locate your business)</h3>
  </SectionTitle>

 <FormRow>
  {/* New: Country Dropdown */}
  <FormGroup error={!!errors.selectedCountry}>
    <Label htmlFor="selectedCountry">Country</Label>
    <Select
      id="selectedCountry"
      value={formData.selectedCountry}
      onChange={(e) => {
        // Clear all location fields if the country changes
        setFormData({
          selectedCountry: e.target.value,
          selectedRegion: '',
          selectedDistrict: '',
          selectedSubcounty: '',
          selectedParish: '',
          selectedVillage: '',
        });
        setErrors(prev => ({...prev, selectedCountry: '' }));
      }}
    >
      <option value="">Select Country</option>
      {allCountries.map((countryName: string, index: number) => (
        <option key={index} value={countryName}>{countryName}</option>
      ))}
    </Select>
    {errors.selectedCountry && <ErrorMessage>{errors.selectedCountry}</ErrorMessage>}
  </FormGroup>

  {/* The rest of the form fields will be disabled until Uganda is selected */}
  <FormGroup error={!!errors.selectedRegion}>
    <Label htmlFor="selectedRegion">Region</Label>
    <Select
      id="selectedRegion"
      value={formData.selectedRegion}
      onChange={(e) => {
        setFormData({
          selectedRegion: e.target.value,
          selectedDistrict: '',
          selectedSubcounty: '',
          selectedParish: '',
          selectedVillage: '',
        });
        setErrors(prev => ({...prev, selectedRegion: '' }));
      }}
      disabled={formData.selectedCountry !== 'Uganda'}
    >
      <option value="">Select Region</option>
      {regions.map(region => (
        <option key={region.id} value={region.name}>{region.name}</option>
      ))}
    </Select>
    {errors.selectedRegion && <ErrorMessage>{errors.selectedRegion}</ErrorMessage>}
  </FormGroup>

  <FormGroup error={!!errors.selectedDistrict}>
    <Label htmlFor="selectedDistrict">District/City</Label>
    <Select
      id="selectedDistrict"
      value={formData.selectedDistrict}
      onChange={(e) => {
        setFormData({
          selectedDistrict: e.target.value,
          selectedSubcounty: '',
          selectedParish: '',
          selectedVillage: '',
        });
        setErrors(prev => ({...prev, selectedDistrict: '' }));
      }}
      disabled={!formData.selectedRegion}
    >
      <option value="">Select District/City</option>
      {districts.map(district => (
        <option key={district.id} value={district.name}>{district.name}</option>
      ))}
    </Select>
    {errors.selectedDistrict && <ErrorMessage>{errors.selectedDistrict}</ErrorMessage>}
  </FormGroup>

  <FormGroup error={!!errors.selectedSubcounty}>
    <Label htmlFor="selectedSubcounty">Subcounty/Division</Label>
    <Select
      id="selectedSubcounty"
      value={formData.selectedSubcounty}
      onChange={(e) => {
        setFormData({
          selectedSubcounty: e.target.value,
          selectedParish: '',
          selectedVillage: '',
        });
        setErrors(prev => ({...prev, selectedSubcounty: '' }));
      }}
      disabled={!formData.selectedDistrict}
    >
      <option value="">Select Subcounty/Division</option>
      {subcounties.map(subcounty => (
        <option key={subcounty.id} value={subcounty.name}>{subcounty.name}</option>
      ))}
    </Select>
    {errors.selectedSubcounty && <ErrorMessage>{errors.selectedSubcounty}</ErrorMessage>}
  </FormGroup>

  <FormGroup error={!!errors.selectedParish}>
    <Label htmlFor="selectedParish">Parish/Ward</Label>
    <Select
      id="selectedParish"
      value={formData.selectedParish}
      onChange={(e) => {
        setFormData({
          selectedParish: e.target.value,
          selectedVillage: '',
        });
        setErrors(prev => ({...prev, selectedParish: '' }));
      }}
      disabled={!formData.selectedSubcounty}
    >
      <option value="">Select Parish/Ward</option>
      {parishes.map(parish => (
        <option key={parish.id} value={parish.name}>{parish.name}</option>
      ))}
    </Select>
    {errors.selectedParish && <ErrorMessage>{errors.selectedParish}</ErrorMessage>}
  </FormGroup>
</FormRow>

<FormRow>
  {/* <FormGroup error={!!errors.selectedVillage}>
    <Label htmlFor="selectedVillage">Village</Label>
    <Select
      id="selectedVillage"
      value={formData.selectedVillage}
      onChange={(e) => {
        setFormData({ selectedVillage: e.target.value });
        setErrors(prev => ({...prev, selectedVillage: '' }));
      }}
      disabled={!formData.selectedParish}
    >
      <option value="">Select Village</option>
      {villages.map((villageName: string, index: number) => (
        <option key={index} value={villageName}>{villageName}</option>
      ))}
    </Select>
    {errors.selectedVillage && <ErrorMessage>{errors.selectedVillage}</ErrorMessage>}
  </FormGroup> */}

    <FormGroup error={!!errors.actualLocation}>
      <Label htmlFor="actualLocation">Village/Actual Location of Business:<RequiredIndicator>*</RequiredIndicator></Label>
      <Input
  type="text"
  id="actualLocation"
  value={formData.actualLocation}
  onChange={(e) => {
    setFormData({actualLocation: e.target.value });
    setErrors(prev => ({...prev, actualLocation: '' }));
  }}
  required
  placeholder="e.g., Shop No. 5, Main Street, near City Mall"
/>
      {errors.actualLocation && <ErrorMessage>{errors.actualLocation}</ErrorMessage>}
    </FormGroup>
  </FormRow>
</FormSection>
        
        <FormSection>
          <SectionTitle>
            <FontAwesomeIcon icon={faImage} />
            <h3>Business Visuals</h3>
          </SectionTitle>

          <FormRow>
            <FormGroup error={!!errors.businessPhoto}>
              <Label htmlFor="businessPhoto">Clear Photo of Your Business Face<RequiredIndicator>*</RequiredIndicator></Label>
              <FileUploadContainer>
                <FileUploadLabel htmlFor="businessPhoto">
                  {formData.businessPhoto ? 'Change Photo' : 'Upload Photo'}
                </FileUploadLabel>
                <FileInput
                  type="file"
                  id="businessPhoto"
                  accept="image/*"
                  onChange={handleBusinessPhotoChange}
                  required
                />
                {formData.businessPhotoPreview && (
                  <ImagePreviewContainer>
                    <PreviewImage src={formData.businessPhotoPreview} alt="Business Preview" />
                  </ImagePreviewContainer>
                )}
              </FileUploadContainer>
              {errors.businessPhoto && <ErrorMessage>{errors.businessPhoto}</ErrorMessage>}
            </FormGroup>

           <FormGroup>
              <Label htmlFor="googleMapLocation">Google Map Location</Label>
              <MapPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={
                  formData.latitude && formData.longitude
                    ? {lat: formData.latitude, lng: formData.longitude }
                    : undefined
                }
              />
                  <Input
                    type="text"
                    id="googleMapDisplay"
                    value={
                      formData.latitude && formData.longitude
                        ? `Lat: ${formData.latitude.toFixed(4)}, Lng: ${formData.longitude.toFixed(4)}`
                        : ''
                    }
                    readOnly
                    placeholder="Select a location on the map"
                  />
            </FormGroup>
          </FormRow>
          
          <FormGroup>
            <Label>Services Offered by Your Business</Label>
            {services.map((service, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                <Input
                  type="text"
                  value={service}
                  onChange={(e) => updateService(index, e.target.value)}
                  placeholder="Enter a service"
                  style={{ flex: 1, marginRight: '10px' }}
                />
                <Button type="button" onClick={() => removeService(index)} disabled={services.length === 1}>Remove</Button>
              </div>
            ))}
            <Button type="button" onClick={addService}>Add Another Service</Button>
          </FormGroup>
          
          <FormGroup>
             <Label>Name of GREMA staff who registered you (Optional)</Label>
            <Input
                type="text"
                id="gremastaff"
                value={formData.gremastaff || ''}
                onChange={(e) => setFormData({gremastaff: e.target.value })}                
              />
             
          </FormGroup>
        </FormSection>
      </FormContentWrapper>

      <Button type="submit" onClick={handleSubmit}>Submit & Proceed to Payment</Button>
    </BuyStallForm>
  );
};

export default StallRegistrationForm;
